import type { TPlan, TUserRole } from "../types";
import type { UserStatusType, UserType } from "@aws-sdk/client-cognito-identity-provider";

import { TOvdNsdConvertersAllowed } from "../organization";

export type ICognitoUser = {
  username: string;
  sub: string;
  email: string;
  name: string;
  familyName: string;
  marketing: boolean;
  userCreateDate?: Date;
  userLastModifiedDate?: Date;
  userStatusType: UserStatusType;
  /** Cognito-level enabled flag (distinct from org-level organizationUserEnabled) */
  cognitoEnabled?: boolean;
  mfaOptions: unknown | undefined;
  /** Live MFA status. Only populated from AdminGetUser; undefined when the user came from ListUsers */
  preferredMfaSetting?: string;
  /** Live MFA status. Only populated from AdminGetUser; undefined when the user came from ListUsers */
  userMFASettingList?: string[];
  mainIdentityId: string;
  isFederated: "1" | "0";
} & (
  | {
      augmented: false;
      organizations: Array<IOrganizationsCognitoAttribute>;
    }
  | {
      augmented: true;
      organizations: Array<IOrganizationsCognitoAttributeAugmented>;
    }
);

/** The data from the Cognito Attribute into JSON */
export interface IOrganizationsCognitoAttribute {
  orgId: string;
  role: TUserRole;
  uEnabled: "0" | "1";
}

/** Once read the data from the DB, the information per Org can be augmented */
export interface IOrganizationsCognitoAttributeAugmented extends IOrganizationsCognitoAttribute {
  name: string;
  expired: "0" | "1";
  expDate: string | undefined;
  planId: TPlan;
  oEnabled: "0" | "1";
  mfaAll: "0" | "1";
  rights: string[];
  domains: string;
  freeTrialDownloaded: string[] | undefined;
  freeTrialMax: number | undefined;
  ovdConverterAllowed: TOvdNsdConvertersAllowed | undefined;
}

export type UserTypeWithMfa = UserType & {
  preferredMfaSetting?: string;
  userMFASettingList?: string[];
};

/** Use to present a User when related to an organization */
export interface ICognitoUserWithOrgData
  extends Omit<ICognitoUser, "organizations" | "augmented">, Record<string, unknown> {
  organizationUserRole: TUserRole;
  organizationUserEnabled: "1" | "0";
  /** Live MFA state on read. On PUT, `false` requests disabling the user's MFA in Cognito */
  mfa?: boolean;
}

/** Whether the user currently has any MFA method active in Cognito */
export function isMfaEnabledForCognitoUser(
  u?: Partial<ICognitoUser>,
): boolean {
  if (!u) return false;
  if (u.userMFASettingList?.length) return true;
  if (u.preferredMfaSetting) return true;
  return Array.isArray(u.mfaOptions) && u.mfaOptions.length > 0; // legacy SMS
}
